import React, { useState, useEffect } from "react";
import {
  listPlayerProfiles,
  listArchivedPlayerProfiles,
  createPlayerProfile,
  updatePlayerProfile,
  archivePlayerProfile,
  restorePlayerProfile,
  linkPlayerToUser,
  PlayerProfile,
} from "../store/supabasePlayerProfiles";
import { supabase } from "../supabaseClient";
import { PlayerAvatar } from "../components/PlayerAvatar";
import { PlayerPhotoUpload } from "../components/PlayerPhotoUpload";
import { formatPlayerName } from "../utils/nameFormatter";
import { getSquadWithPlayers, listTeams, SquadFormat } from "../store/teams";
import CreatePlayerProfile from "./CreatePlayerProfile";
import "./DirectoryPages.css";

interface Props {
  onBack: () => void;
}

const CricketSquad: React.FC<Props> = ({ onBack }) => {
  const [players, setPlayers] = useState<PlayerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<PlayerProfile | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerProfile | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "matches" | "recent">("name");
  const [formatFilter, setFormatFilter] = useState<"all" | SquadFormat>("all");
  const [playerFormats, setPlayerFormats] = useState<Record<string, SquadFormat[]>>({});
  const [playerTeams, setPlayerTeams] = useState<Record<string, string[]>>({});
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedPlayers, setArchivedPlayers] = useState<PlayerProfile[]>([]);

  useEffect(() => {
    loadPlayers();
  }, []);

  async function loadPlayers() {
    setLoading(true);
    try {
      const [profiles, archived, teamGroups] = await Promise.all([
        listPlayerProfiles(),
        listArchivedPlayerProfiles(),
        Promise.all((["indoor", "t20", "odi"] as SquadFormat[]).map((format) => listTeams(format))),
      ]);
      const memberships = await Promise.all(teamGroups.flat().map(async (team) => ({ team, squad: await getSquadWithPlayers(team.id, team.format) })));
      const formats: Record<string, SquadFormat[]> = {};
      const teamNames: Record<string, string[]> = {};
      // Seed formats from each profile's explicitly-registered formats column,
      // then merge in formats implied by team squad memberships.
      profiles.forEach((p) => {
        if (Array.isArray(p.formats) && p.formats.length > 0) {
          formats[p.id] = p.formats.filter((f) =>
            ["indoor", "t20", "odi"].includes(f)
          ) as SquadFormat[];
        }
      });
      archived.forEach((p) => {
        if (Array.isArray(p.formats) && p.formats.length > 0) {
          formats[p.id] = p.formats.filter((f) =>
            ["indoor", "t20", "odi"].includes(f)
          ) as SquadFormat[];
        }
      });
      memberships.forEach(({ team, squad }) => squad?.players.forEach((player) => {
        formats[player.id] = Array.from(new Set([...(formats[player.id] || []), team.format]));
        teamNames[player.id] = Array.from(new Set([...(teamNames[player.id] || []), team.name]));
      }));
      setPlayers(profiles);
      setArchivedPlayers(archived);
      setPlayerFormats(formats);
      setPlayerTeams(teamNames);
    } finally {
      setLoading(false);
    }
  }

  function handleViewPlayer(player: PlayerProfile) {
    setSelectedPlayer(player);
  }

  function getPlayerInitials(name: string): string {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  const filteredPlayers = players.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFormat = formatFilter === "all" || (playerFormats[p.id] || []).includes(formatFilter);
    return matchesSearch && matchesFormat;
  });

  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    if (sortBy === "name") {
      return a.name.localeCompare(b.name);
    }
    return 0;
  });

  const playerCountFor = (filter: "all" | SquadFormat): number => filter === "all"
    ? players.length
    : players.filter((player) => (playerFormats[player.id] || []).includes(filter)).length;

  if (loading) return <div className="ww-directory ww-directory--loading">Loading Player Profiles...</div>;

  if (selectedPlayer) {
    return (
      <PlayerDetailsView
        player={selectedPlayer}
        onBack={() => {
          setSelectedPlayer(null);
        }}
        onEdit={() => {
          setEditingPlayer(selectedPlayer);
          setSelectedPlayer(null);
        }}
        onArchive={async () => {
          const confirmed = confirm(
            `Archive ${selectedPlayer.name}?\n\nThe player will be excluded from new match selection but all historical records, statistics and completed match scorecards are preserved. You can restore them at any time.`
          );
          if (confirmed) {
            await archivePlayerProfile(selectedPlayer.id);
            setSelectedPlayer(null);
            loadPlayers();
          }
        }}
        onRestore={async () => {
          await restorePlayerProfile(selectedPlayer.id);
          setSelectedPlayer(null);
          loadPlayers();
        }}
      />
    );
  }

  if (showCreateWizard) {
    return (
      <CreatePlayerProfile
        onBack={() => {
          setShowCreateWizard(false);
          loadPlayers();
        }}
      />
    );
  }

  if (editingPlayer) {
    return (
      <CreatePlayerProfile
        editPlayer={editingPlayer}
        onBack={() => {
          setEditingPlayer(null);
          loadPlayers();
        }}
      />
    );
  }

  if (showAddModal) {
    return (
      <PlayerFormModal
        player={editingPlayer}
        onClose={() => {
          setShowAddModal(false);
          setEditingPlayer(null);
        }}
        onSave={async (playerData) => {
          try {
            if (editingPlayer) {
              await updatePlayerProfile(editingPlayer.id, playerData);
            } else {
              await createPlayerProfile(playerData);
            }
            setShowAddModal(false);
            setEditingPlayer(null);
            await loadPlayers();
          } catch (err: any) {
            console.error("[CricketSquad] Error saving player:", err);
            alert(err.message || "Failed to save player. Please try again.");
            throw err;
          }
        }}
      />
    );
  }

  return (
    <div className="ww-directory">
      <main className="ww-directory__content">
        <div className="ww-directory__heading">
          <div><h1>Player Profiles</h1><p>View and manage all players across your teams.</p></div>
          <button className="ww-directory__primary" onClick={() => setShowCreateWizard(true)}>+ Create Player Profile</button>
        </div>
        <label className="ww-directory__search"><span aria-hidden="true">⌕</span><input aria-label="Search players by name" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search players by name..." /></label>
        <div className="ww-directory__tabs" role="tablist" aria-label="Player format filters">
          {(["all", "indoor", "t20", "odi"] as const).map((filter) => (
            <button key={filter} role="tab" aria-selected={formatFilter === filter} className={formatFilter === filter ? "is-active" : ""} onClick={() => setFormatFilter(filter)}>
              {filter === "all" ? "All Players" : filter.toUpperCase()} <span>({playerCountFor(filter)})</span>
            </button>
          ))}
        </div>
        {sortedPlayers.length === 0 ? (
          <div className="ww-directory__empty"><h2>{searchQuery ? "No players match your search" : "No player profiles yet"}</h2><p>{searchQuery ? "Try a different name or format." : "Add your first player to build a squad."}</p>{!searchQuery && <button className="ww-directory__primary" onClick={() => setShowCreateWizard(true)}>Create First Player</button>}</div>
        ) : (
          <div className="ww-directory__list">
            {sortedPlayers.map((player) => (
              <article key={player.id} className="ww-directory-card ww-player-card" onClick={() => handleViewPlayer(player)}>
                <PlayerAvatar name={player.name} photoUrl={player.photo_url} size="lg" className="ww-player-card__avatar" />
                <div className="ww-directory-card__body"><h2>{formatPlayerName(player.name)}</h2><div className="ww-player-card__details"><span>▰ {player.batting_style === "LHB" ? "Left Hand Bat" : "Right Hand Bat"}</span><span>{player.bowling_style || "Bowling style not set"}</span></div><div className="ww-player-card__teams">♟ {playerTeams[player.id]?.length ? playerTeams[player.id].join(", ") : "No team assigned"}</div></div>
                <div className="ww-directory-card__actions"><button aria-label={`Actions for ${player.name}`} onClick={(event) => { event.stopPropagation(); setOpenMenu(openMenu === player.id ? null : player.id); }}>⋮</button>{openMenu === player.id && <div className="ww-directory-menu"><button onClick={(event) => { event.stopPropagation(); setEditingPlayer(player); setOpenMenu(null); }}>Edit</button><button onClick={(event) => { event.stopPropagation(); setSelectedPlayer(player); setOpenMenu(null); }}>Open profile</button><button className="is-danger" onClick={(event) => { event.stopPropagation(); setOpenMenu(null); if (confirm(`Archive ${player.name}?\n\nHistorical records and statistics are preserved. You can restore them at any time.`)) { archivePlayerProfile(player.id).then(() => { setSelectedPlayer(null); loadPlayers(); }); } }}>Archive</button></div>}<button aria-label={`Open ${player.name} profile`} onClick={(event) => { event.stopPropagation(); handleViewPlayer(player); }}>›</button></div>
              </article>
            ))}
          </div>
        )}
        {archivedPlayers.length > 0 && (
          <div className="ww-directory__archived-section" style={{ marginTop: "2rem" }}>
            <button
              className="ww-directory__archived-toggle"
              style={{
                width: "100%",
                textAlign: "left",
                background: "transparent",
                border: "1px solid var(--ww-border, #334155)",
                borderRadius: "0.75rem",
                padding: "0.75rem 1rem",
                color: "var(--ww-text-secondary, #94a3b8)",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              onClick={() => setShowArchived(!showArchived)}
            >
              <span>Archived Players ({archivedPlayers.length})</span>
              <span>{showArchived ? "▲" : "▼"}</span>
            </button>
            {showArchived && (
              <div className="ww-directory__list" style={{ marginTop: "0.75rem" }}>
                {archivedPlayers.map((player) => (
                  <article
                    key={player.id}
                    className="ww-directory-card ww-player-card"
                    style={{ opacity: 0.6 }}
                    onClick={() => handleViewPlayer(player)}
                  >
                    <PlayerAvatar name={player.name} photoUrl={player.photo_url} size="lg" className="ww-player-card__avatar" />
                    <div className="ww-directory-card__body">
                      <h2>{formatPlayerName(player.name)}</h2>
                      <div className="ww-player-card__details">
                        <span style={{ color: "#f59e0b", fontWeight: 600 }}>Archived</span>
                      </div>
                    </div>
                    <div className="ww-directory-card__actions">
                      <button
                        aria-label={`Restore ${player.name}`}
                        className="ww-directory__primary"
                        style={{ fontSize: "0.8rem", padding: "0.4rem 0.8rem" }}
                        onClick={(event) => {
                          event.stopPropagation();
                          restorePlayerProfile(player.id).then(() => loadPlayers());
                        }}
                      >
                        Restore
                      </button>
                      <button aria-label={`Open ${player.name} profile`} onClick={(event) => { event.stopPropagation(); handleViewPlayer(player); }}>›</button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

interface PlayerFormModalProps {
  player: PlayerProfile | null;
  onClose: () => void;
  onSave: (data: Omit<PlayerProfile, "id" | "user_id" | "created_at" | "updated_at">) => Promise<void>;
}

const PlayerFormModal: React.FC<PlayerFormModalProps> = ({ player, onClose, onSave }) => {
  const [name, setName] = useState(player?.name || "");
  const [nickname, setNickname] = useState(player?.nickname || "");
  const [role, setRole] = useState<PlayerProfile["role"]>(player?.role || "Batsman");
  const [battingStyle, setBattingStyle] = useState<PlayerProfile["batting_style"]>(
    player?.batting_style || "RHB"
  );
  const [bowlingStyle, setBowlingStyle] = useState(player?.bowling_style || "");
  const [photoUrl, setPhotoUrl] = useState(player?.photo_url || "");
  const [jerseyNumber, setJerseyNumber] = useState(player?.jersey_number?.toString() || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Player name is required");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await onSave({
        name: name.trim(),
        nickname: nickname.trim() || undefined,
        role,
        batting_style: battingStyle,
        bowling_style: bowlingStyle.trim() || undefined,
        photo_url: photoUrl.trim() || undefined,
        jersey_number: jerseyNumber ? parseInt(jerseyNumber) : undefined,
        is_guest: false,
      });
      // Success - modal will be closed by parent
    } catch (err: any) {
      console.error("[PlayerFormModal] Error saving player:", err);
      setError(err.message || "Failed to save player. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4 pb-24">
      <div className="bg-slate-800 rounded-xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl">
        <div className="bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between rounded-t-xl flex-shrink-0">
          <h2 className="text-2xl font-bold">
            {player ? "Edit Player" : "Add New Player"}
          </h2>
          <button
            onClick={onClose}
            className="text-2xl hover:bg-slate-700 rounded-lg p-2 transition-colors"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden min-h-0">
          <div className="flex-1 overflow-y-auto p-6 space-y-6 overscroll-contain">
            <div>
              <label className="block text-sm font-semibold mb-2">Full Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Nickname</label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Optional"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Role *</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as PlayerProfile["role"])}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="Batsman">Batsman</option>
                  <option value="Bowler">Bowler</option>
                  <option value="All-rounder">All-rounder</option>
                  <option value="Keeper">Keeper</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Batting Style *</label>
                <select
                  value={battingStyle}
                  onChange={(e) => setBattingStyle(e.target.value as PlayerProfile["batting_style"])}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="RHB">Right-handed (RHB)</option>
                  <option value="LHB">Left-handed (LHB)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Bowling Style</label>
              <input
                type="text"
                value={bowlingStyle}
                onChange={(e) => setBowlingStyle(e.target.value)}
                placeholder="e.g., Right-arm fast, Left-arm spin"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Jersey Number</label>
              <input
                type="number"
                value={jerseyNumber}
                onChange={(e) => setJerseyNumber(e.target.value)}
                placeholder="Optional"
                min="1"
                max="999"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <PlayerPhotoUpload
              currentPhotoUrl={photoUrl}
              playerId={player?.id}
              playerName={name || "New Player"}
              onPhotoUploaded={(url) => setPhotoUrl(url)}
              onPhotoRemoved={() => setPhotoUrl("")}
            />
          </div>

          <div className="border-t border-slate-700 bg-slate-800 px-6 py-4 flex-shrink-0 rounded-b-xl">
            {error && (
              <div className="mb-4 bg-red-900/50 border border-red-700 rounded-lg px-4 py-3 text-red-200 text-sm">
                {error}
              </div>
            )}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1 bg-slate-700 hover:bg-slate-600 px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!name.trim() || saving}
                className="flex-1 bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {saving ? "Saving..." : player ? "Update Player" : "Add Player"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

interface PlayerDetailsViewProps {
  player: PlayerProfile;
  onBack: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onRestore: () => void;
}

const PlayerDetailsView: React.FC<PlayerDetailsViewProps> = ({
  player,
  onBack,
  onEdit,
  onArchive,
  onRestore,
}) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkedUserEmail, setLinkedUserEmail] = useState<string>("");
  const [currentPlayer, setCurrentPlayer] = useState<PlayerProfile>(player);

  useEffect(() => {
    loadLinkedUserEmail();
  }, [player]);

  async function loadLinkedUserEmail() {
    if (player.linked_user_id) {
      const { data } = await supabase
        .from("profiles")
        .select("full_name, email")
        .eq("id", player.linked_user_id)
        .maybeSingle();

      if (data) {
        setLinkedUserEmail(data.full_name || data.email || "Unknown User");
      } else {
        const { data: authUser } = await supabase.auth.admin.getUserById(
          player.linked_user_id
        );
        if (authUser) {
          setLinkedUserEmail(authUser.user?.email || "Unknown User");
        }
      }
    }
  }

  async function handleUnlink() {
    const confirmed = confirm(
      `Are you sure you want to unlink ${currentPlayer.name} from their user account?`
    );
    if (confirmed) {
      const updated = await linkPlayerToUser(currentPlayer.id, null);
      if (updated) {
        setCurrentPlayer(updated);
        setLinkedUserEmail("");
      }
    }
  }

  function getPlayerInitials(name: string): string {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="bg-slate-800 py-4 shadow-md sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="text-2xl hover:bg-slate-700 rounded-lg p-2 transition-colors"
            >
              ←
            </button>
            <h1 className="text-2xl font-bold">Player Profile</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onEdit}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              Edit
            </button>
            {currentPlayer.is_archived ? (
              <button
                onClick={onRestore}
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Restore
              </button>
            ) : (
              <button
                onClick={onArchive}
                className="bg-amber-600 hover:bg-amber-700 px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Archive
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center gap-6">
            <PlayerAvatar
              name={currentPlayer.name}
              photoUrl={currentPlayer.photo_url}
              size="xl"
            />
            <div className="flex-1">
              <h2 className="text-3xl font-bold">{currentPlayer.name}</h2>
              {currentPlayer.is_archived && (
                <span className="inline-block mt-1 bg-amber-600/20 text-amber-400 text-sm font-semibold px-3 py-1 rounded-lg border border-amber-600/30">
                  Archived
                </span>
              )}
              {currentPlayer.nickname && (
                <p className="text-lg text-slate-400">"{currentPlayer.nickname}"</p>
              )}
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="bg-slate-700 px-3 py-1 rounded-lg text-sm font-semibold">
                  {currentPlayer.role}
                </span>
                <span className="bg-slate-700 px-3 py-1 rounded-lg text-sm">
                  {currentPlayer.batting_style}
                </span>
                {currentPlayer.bowling_style && (
                  <span className="bg-slate-700 px-3 py-1 rounded-lg text-sm">
                    {currentPlayer.bowling_style}
                  </span>
                )}
                {currentPlayer.jersey_number && (
                  <span className="bg-slate-700 px-3 py-1 rounded-lg text-sm">
                    #{currentPlayer.jersey_number}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold">Account Linking</h3>
              <p className="text-sm text-slate-400">
                Link this player profile to a user account
              </p>
            </div>
          </div>
          {currentPlayer.linked_user_id ? (
            <div className="flex items-center justify-between bg-slate-900 rounded-lg p-4 border border-slate-700">
              <div>
                <div className="text-sm text-slate-400">Linked to</div>
                <div className="text-white font-semibold">{linkedUserEmail}</div>
              </div>
              <button
                onClick={handleUnlink}
                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Unlink
              </button>
            </div>
          ) : (
            <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
              <p className="text-slate-400 mb-4">
                This player profile is not linked to any user account. Link it to allow the player to view their stats.
              </p>
              <button
                onClick={() => setShowLinkModal(true)}
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Link to User Account
              </button>
            </div>
          )}
        </div>

        {showLinkModal && (
          <LinkPlayerModal
            player={currentPlayer}
            onClose={() => setShowLinkModal(false)}
            onLinked={(updated) => {
              setCurrentPlayer(updated);
              setShowLinkModal(false);
              loadLinkedUserEmail();
            }}
          />
        )}

        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h3 className="text-lg font-bold">Career Stats</h3>
          </div>
          <div className="p-8 text-center">
            <div className="text-4xl mb-3">📊</div>
            <p className="text-slate-400 text-sm">Stats coming soon</p>
          </div>
        </div>
      </main>
    </div>
  );
};


interface LinkPlayerModalProps {
  player: PlayerProfile;
  onClose: () => void;
  onLinked: (player: PlayerProfile) => void;
}

const LinkPlayerModal: React.FC<LinkPlayerModalProps> = ({ player, onClose, onLinked }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleLink() {
    if (!email.trim()) {
      setError("Please enter an email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { data: profiles, error: searchError } = await supabase
        .from("profiles")
        .select("id, email")
        .ilike("email", email.trim())
        .limit(1);

      if (searchError) {
        console.error("Error searching for user:", searchError);
        setError("An error occurred while searching for the user");
      } else if (profiles && profiles.length > 0) {
        const updated = await linkPlayerToUser(player.id, profiles[0].id);
        if (updated) {
          onLinked(updated);
        } else {
          setError("Failed to link player profile");
        }
      } else {
        setError("No WatchWicket account found for that email");
      }
    } catch (err) {
      console.error("Error linking player:", err);
      setError("An error occurred while linking the player");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl max-w-md w-full">
        <div className="border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Link Player to User Account</h2>
          <button
            onClick={onClose}
            className="text-2xl hover:bg-slate-700 rounded-lg p-2 transition-colors"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <p className="text-sm text-slate-400 mb-4">
              Enter the email address of the user account you want to link to{" "}
              <span className="font-semibold text-white">{formatPlayerName(player.name)}</span>.
            </p>
            <label className="block text-sm font-semibold mb-2">User Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
              }}
              placeholder="user@example.com"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
          </div>

          <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
            <p className="text-xs text-slate-400">
              The user must have an account on WatchWicket. Once linked, they will be able to view
              their stats from matches where you've enabled stat visibility.
            </p>
          </div>

          <div className="flex gap-4 pt-2">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 bg-slate-700 hover:bg-slate-600 px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleLink}
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              {loading ? "Linking..." : "Link Account"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CricketSquad;
